import React from 'react';
import { useMemo } from 'react';
import { useEffect } from 'react';
import { uiEnvironment } from 'apps/sigma2-ui/src/constants/environment';
import { AppPage } from 'apps/sigma2-ui/src/constants/page';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { LogoSvg } from 'apps/sigma2-ui/src/components/svg/Logo';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { LinkButton } from 'apps/sigma2-ui/src/components/ui/LinkButton';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { RoutableLink } from 'apps/sigma2-ui/src/components/ui/RoutableLink';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';

interface AdminLayoutProps {
  currentPage: AppPage;
  children: React.ReactNode;
  dataTestId?: string;
}

export interface AdminTableTextColumn {
  columnType: 'text';
  text: string;
  key: string;
}

export interface AdminTableButtonColumn {
  columnType: 'button';
  buttonText: string;
  key: string;
  ariaLabel?: string;
  onClick: () => void;
}

export interface AdminTableInputColumn {
  columnType: 'input';
  key: string;
  value: string;
  onChange: (newValue: string) => void;
}

export interface AdminTableSelectColumn {
  columnType: 'select';
  key: string;
  value: string;
  options: { key: string; value: string }[];
  onChange: (newValue: string) => void;
}

export interface AdminTableBooleanColumn {
  columnType: 'boolean';
  key: string;
  value: boolean;
  onChange: (newValue: boolean) => void;
}

export interface AdminTableCustomColumn {
  columnType: 'custom';
  key: string;
  ui: React.ReactNode;
}

export type AdminTableColumn =
  | AdminTableTextColumn
  | AdminTableButtonColumn
  | AdminTableInputColumn
  | AdminTableSelectColumn
  | AdminTableBooleanColumn
  | AdminTableCustomColumn;

export interface AdminTableRow {
  key: string;
  columns: AdminTableColumn[];
}
export interface AdminTableProps {
  headings: string[];
  rows: AdminTableRow[];
  backgroundColorFn?: (key: string) => string | undefined;
  dataTestId?: string;
}

export const AdminTable = (props: AdminTableProps) => {
  const { headings, backgroundColorFn, rows, dataTestId } = props;
  const language = useLanguage();

  return (
    <div className="overflow-x-auto" data-testid={dataTestId}>
      <table className="table-auto mt-4">
        <tbody>
          <tr>
            {headings.map((heading) => {
              return (
                <td
                  key={heading}
                  className="bg-blue-300 border border-primary text-center px-8 py-4"
                >
                  {heading}
                </td>
              );
            })}
          </tr>
          {rows.map((row) => {
            const { columns, key } = row;

            const columnChildren = columns.map((column) => {
              let innerElement: React.ReactNode;

              if (column.columnType === 'text') {
                innerElement = <>{column.text}</>;
              } else if (column.columnType === 'input') {
                innerElement = (
                  <input
                    className="form-input"
                    type="text"
                    key={key}
                    value={column.value}
                    aria-label={column.key}
                    onChange={(event) => {
                      column.onChange(event.target.value);
                    }}
                  />
                );
              } else if (column.columnType === 'boolean') {
                innerElement = (
                  <input
                    className="form-checkbox"
                    type="checkbox"
                    checked={column.value}
                    aria-label={column.key}
                    onChange={(event) => {
                      column.onChange(event.target.checked);
                    }}
                  />
                );
              } else if (column.columnType === 'select') {
                innerElement = (
                  <select
                    value={column.value}
                    aria-label={column.key}
                    className="form-select w-full"
                    onChange={(event) => {
                      column.onChange(event.target.value);
                    }}
                  >
                    {column.options.map((option) => {
                      return (
                        <option key={option.key} value={option.value}>
                          {option.key}
                        </option>
                      );
                    })}
                  </select>
                );
              } else if (column.columnType === 'button') {
                innerElement = (
                  <LegacyButton
                    variant={LegacyButtonVariant.PRIMARY_LIGHT}
                    onClick={column.onClick}
                    className="text-center"
                    aria-label={column.ariaLabel}
                  >
                    {column.buttonText}
                  </LegacyButton>
                );
              } else {
                const { ui } = column;
                innerElement = ui;
              }
              return (
                <td
                  key={column.key}
                  className="bg-transparent border border-primary text-center p-4"
                >
                  {innerElement}
                </td>
              );
            });

            const backgroundColor = backgroundColorFn?.(key) ?? 'bg-white';
            return (
              <tr className={backgroundColor} key={key}>
                {columnChildren}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const AdminLayout = ({ children, dataTestId }: AdminLayoutProps) => {
  const language = useLanguage();
  const { startLoggingOut, myUser } = useUser();
  const navigate = useNavigateSafe();

  const isSuperOrOrgAdmin = useMemo(() => {
    if (!myUser) {
      return false;
    }

    if (myUser.roleAssignments === 'superAdmin') {
      return true;
    }

    return myUser.roleAssignments.some((roleAssignment) => {
      return roleAssignment.organizationRole === 'admin';
    });
  }, [myUser]);

  useAuthenticatedPageRedirect();

  // The redirect above is more general, this one is for the admin panel specifically.
  useEffect(() => {
    if (myUser && !isSuperOrOrgAdmin) {
      // This user is not allowed in the admin panel, so redirect them to the overview page.
      navigate('/overview');
    }
  }, [isSuperOrOrgAdmin, myUser, navigate]);

  if (!isSuperOrOrgAdmin) {
    // We may still be loading, but we don't want to show the admin panel yet.
    return <></>;
  }

  return (
    <div
      className="mx-auto w-full pl-6 pt-6 pr-6 relative flex flex-row"
      style={{
        // Set a simple min-width of 768px for basic support of tablets (but not phones)
        minWidth: '64rem',
      }}
      data-testid={dataTestId}
    >
      <div className="bg-white rounded-lg shadow-2px w-40 flex flex-col fixed top-4 left-4 z-standout">
        <RoutableLink
          to="/"
          className="h-20 min-h-20 bg-primary rounded-t-lg flex items-center justify-center mb-2"
          data-qa="logoMainNavLink"
          style={{
            backgroundImage: `linear-gradient(136deg, #012B56 50%, #076DD3 161.31%)`,
          }}
        >
          <LogoSvg
            svg={{
              className: 'w-12 text-white',
              'aria-label': 'Sigma Squared Logo',
            }}
          />
        </RoutableLink>

        <div className="flex flex-col p-2">
          <LinkButton
            href="/groups"
            data-qa={'groupsBtn'}
            className="text-center mb-2"
            variant={LegacyButtonVariant.PRIMARY_LIGHT}
          >
            Groups
          </LinkButton>

          {myUser?.roleAssignments === 'superAdmin' && (
            <>
              <LinkButton
                href="/users"
                data-qa={'usersBtn'}
                className="text-center mb-2"
                variant={LegacyButtonVariant.PRIMARY_LIGHT}
              >
                Users
              </LinkButton>
              <LinkButton
                href="/organizations"
                data-qa={'organizationsBtn'}
                className="text-center mb-2"
                variant={LegacyButtonVariant.PRIMARY_LIGHT}
              >
                Organizations
              </LinkButton>
              <LinkButton
                href="/schedules"
                data-qa={'schedulesBtn'}
                className="text-center mb-2"
                variant={LegacyButtonVariant.PRIMARY_LIGHT}
              >
                Schedules
              </LinkButton>
              <LinkButton
                href="/emails"
                data-qa={'emailsBtn'}
                variant={LegacyButtonVariant.PRIMARY_LIGHT}
                className="text-center mb-2"
              >
                Emails
              </LinkButton>
              <LinkButton
                href="/sso-main"
                data-qa={'mainSsoBtn'}
                variant={LegacyButtonVariant.PRIMARY_LIGHT}
                className="text-center mb-2"
              >
                SSO
              </LinkButton>
              <LinkButton
                href="/feature-flags"
                data-qa={'featureFlagsBtn'}
                variant={LegacyButtonVariant.PRIMARY_LIGHT}
                className="text-center mb-2"
              >
                Feature Flags
              </LinkButton>
              <LinkButton
                href="/audits"
                data-qa={'auditsBtn'}
                variant={LegacyButtonVariant.PRIMARY_LIGHT}
                className="text-center mb-2"
              >
                Audits
              </LinkButton>
              <LinkButton
                href="/external-streamlit-apps"
                data-qa={'streamlitAppsBtn'}
                variant={LegacyButtonVariant.PRIMARY_LIGHT}
                className="text-center mb-2"
              >
                Streamlit Apps
              </LinkButton>
              <LinkButton
                href="/external-dagster-transforms"
                data-qa={'dagsterTransformsBtn'}
                variant={LegacyButtonVariant.PRIMARY_LIGHT}
                className="text-center mb-2"
              >
                Transforms
              </LinkButton>
              <LinkButton
                href="/data-generator"
                data-qa={'dataGeneratorBtn'}
                variant={LegacyButtonVariant.PRIMARY_LIGHT}
                className="text-center mb-2"
              >
                {language.dataGeneratorPageTitle()}
              </LinkButton>
            </>
          )}

          {isSuperOrOrgAdmin && (
            <LinkButton
              href="/connections"
              data-qa={'connectionsBtn'}
              className="text-center mb-2"
              variant={LegacyButtonVariant.PRIMARY_LIGHT}
            >
              {language.connectionsPageTitle()}
            </LinkButton>
          )}

          <LegacyButton
            onClick={() => {
              startLoggingOut(uiEnvironment.callbackLogoutUri);
            }}
            variant={LegacyButtonVariant.PRIMARY_LIGHT}
          >
            {language.logOut()}
          </LegacyButton>
        </div>
      </div>
      <main className="w-full pl-44 pr-7">{children}</main>
    </div>
  );
};
